/**
 * Client-Side Instant Extraction
 * Extracts SEO data directly in the browser using native DOMParser
 * ⚡ Returns results in ~100-200ms
 */

export interface InstantMetrics {
  title: string;
  description: string;
  canonical: string;
  h1: string;
  h1Count: number;
  h2Count: number;
  h3Count: number;
  images: {
    total: number;
    withAlt: number;
    missingAlt: number;
  };
  links: {
    total: number;
    internal: number;
    external: number;
    nofollow: number;
  };
  wordCount: number;
  isHTTPS: boolean;
  hasViewport: boolean;
  hasFavicon: boolean;
  hasStructuredData: boolean;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  hasServiceWorker: boolean;
  hasCriticalCSS: boolean;
  extractionMethod: 'client-side' | 'server-side';
  timestamp: number;
  responseTime: string;
}

export class ClientSideExtractor {
  /**
   * Fetch and parse HTML directly in the browser
   * ⚡ Returns results in ~100-200ms
   */
  static async extractInstant(url: string): Promise<InstantMetrics> {
    const startTime = Date.now();
    
    try {
      // Try to fetch HTML - CORS will fail for most external sites, that's OK
      // We'll gracefully fall back to server-side extraction
      let response: Response;
      let html: string;
      
      // Create abort controller for timeout (AbortSignal.timeout may not be available in all browsers)
      const controller = new AbortController();
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      
      try {
        // Set timeout
        timeoutId = setTimeout(() => {
          controller.abort();
        }, 5000); // 5 second timeout
        
        // First try with CORS mode (will work for same-origin or CORS-enabled sites)
        response = await fetch(url, {
          mode: 'cors',
          headers: {
            'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
          },
          signal: controller.signal
        });
        
        // Clear timeout on success
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        html = await response.text();
      } catch (fetchError: any) {
        // Clear timeout if it was set
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        // CORS error or network error - this is expected for most external sites
        // Return minimal metrics and let server-side extraction handle it
        if (fetchError.name === 'AbortError' || fetchError.message?.includes('aborted')) {
          console.log('Client-side fetch timeout, will use server-side extraction');
        } else {
          console.log('Client-side fetch failed (CORS/network), will use server-side extraction:', fetchError.message);
        }
        throw new Error('CORS_BLOCKED'); // Special error code to indicate CORS issue
      }
      const responseTime = Date.now() - startTime;
      
      // Parse with DOMParser (native browser API - FAST)
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract metadata instantly
      const baseUrl = new URL(url);
      const baseDomain = baseUrl.hostname;
      
      return {
        // Basic Meta
        title: doc.title || '',
        description: doc.querySelector('meta[name="description"]')?.getAttribute('content') || '',
        canonical: doc.querySelector('link[rel="canonical"]')?.getAttribute('href') || '',
        
        // Headings
        h1: doc.querySelector('h1')?.textContent?.trim() || '',
        h1Count: doc.querySelectorAll('h1').length,
        h2Count: doc.querySelectorAll('h2').length,
        h3Count: doc.querySelectorAll('h3').length,
        
        // Images
        images: {
          total: doc.querySelectorAll('img').length,
          withAlt: doc.querySelectorAll('img[alt]').length,
          missingAlt: doc.querySelectorAll('img:not([alt])').length
        },
        
        // Links
        links: {
          total: doc.querySelectorAll('a[href]').length,
          internal: this.countInternalLinks(doc, baseDomain),
          external: this.countExternalLinks(doc, baseDomain),
          nofollow: doc.querySelectorAll('a[rel*="nofollow"]').length
        },
        
        // Content
        wordCount: this.countWords(doc.body?.textContent || ''),
        
        // Technical
        isHTTPS: url.startsWith('https://'),
        hasViewport: !!doc.querySelector('meta[name="viewport"]'),
        hasFavicon: !!doc.querySelector('link[rel*="icon"]'),
        hasStructuredData: !!doc.querySelector('script[type="application/ld+json"]'),
        
        // Open Graph
        ogTitle: doc.querySelector('meta[property="og:title"]')?.getAttribute('content') || '',
        ogDescription: doc.querySelector('meta[property="og:description"]')?.getAttribute('content') || '',
        ogImage: doc.querySelector('meta[property="og:image"]')?.getAttribute('content') || '',
        
        // Performance hints
        hasServiceWorker: html.includes('serviceWorker.register') || html.includes('navigator.serviceWorker'),
        hasCriticalCSS: html.includes('<style>') && html.indexOf('<style>') < 5000,
        
        // Extraction metadata
        extractionMethod: 'client-side',
        timestamp: Date.now(),
        responseTime: `${responseTime}ms`
      };
      
    } catch (error: any) {
      // CORS errors are expected for external sites - don't log as error
      if (error.message === 'CORS_BLOCKED' || error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        console.log('Client-side extraction blocked by CORS (expected for external sites)');
      } else {
        console.error('Client-side extraction failed:', error);
      }
      
      // Return minimal metrics on error - server will handle extraction
      return {
        title: '',
        description: '',
        canonical: '',
        h1: '',
        h1Count: 0,
        h2Count: 0,
        h3Count: 0,
        images: { total: 0, withAlt: 0, missingAlt: 0 },
        links: { total: 0, internal: 0, external: 0, nofollow: 0 },
        wordCount: 0,
        isHTTPS: url.startsWith('https://'),
        hasViewport: false,
        hasFavicon: false,
        hasStructuredData: false,
        ogTitle: '',
        ogDescription: '',
        ogImage: '',
        hasServiceWorker: false,
        hasCriticalCSS: false,
        extractionMethod: 'client-side',
        timestamp: Date.now(),
        responseTime: 'error'
      };
    }
  }
  
  /**
   * Check if page is client-side rendered (needs Puppeteer)
   */
  static needsPuppeteer(metrics: InstantMetrics): boolean {
    // If critical content is missing, likely CSR
    return (
      !metrics.title ||
      metrics.title.length < 3 ||
      !metrics.h1 ||
      metrics.h1.length < 3 ||
      metrics.wordCount < 50
    );
  }
  
  private static countInternalLinks(doc: Document, baseDomain: string): number {
    return Array.from(doc.querySelectorAll('a[href]'))
      .filter(a => {
        const href = a.getAttribute('href');
        if (!href) return false;
        if (href.startsWith('/') || href.startsWith('#')) return true; // Relative link
        try {
          const linkUrl = new URL(href, window.location.href);
          return linkUrl.hostname === baseDomain || linkUrl.hostname === `www.${baseDomain}` || `www.${linkUrl.hostname}` === baseDomain;
        } catch {
          return false;
        }
      })
      .length;
  }
  
  private static countExternalLinks(doc: Document, baseDomain: string): number {
    return Array.from(doc.querySelectorAll('a[href]'))
      .filter(a => {
        const href = a.getAttribute('href');
        if (!href || href.startsWith('/') || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return false;
        try {
          const linkUrl = new URL(href, window.location.href);
          return linkUrl.hostname !== baseDomain && linkUrl.hostname !== `www.${baseDomain}` && `www.${linkUrl.hostname}` !== baseDomain;
        } catch {
          return false;
        }
      })
      .length;
  }
  
  private static countWords(text: string): number {
    return text
      .trim()
      .split(/\s+/)
      .filter(word => word.length > 0)
      .length;
  }
}

